/**
 * Overlays globaux (feuilles, modales, recherche plein écran).
 *
 * BUG RÉEL CORRIGÉ (téléphone) — un dialogue rendu DANS la topbar restait
 * prisonnier de son contexte de superposition :
 *
 *   .app-topbar  → position: sticky + z-index: 20  ⇒ contexte de superposition
 *   .bottom-nav  → position: fixed  + z-index: 30
 *   .sheet-backdrop (enfant de la topbar) → z-index: 60 … mais RELATIF à la
 *   topbar, donc plafonné à 20 dans l'empilement global.
 *
 * Conséquence observée : le menu utilisateur s'affichait, mais un clic sur
 * « Se déconnecter » atterrissait sur l'onglet « Temps » de la bottom-nav
 * (`document.elementFromPoint()` renvoyait l'icône de la nav). Aucune valeur de
 * `z-index` ne peut corriger cela depuis l'intérieur du contexte parent.
 *
 * Correctif : tout overlay est rendu par un PORTAL vers `#overlay-root`, ajouté
 * en dernier enfant de `<body>` — donc hors de tout contexte de superposition
 * applicatif. Il passe alors réellement devant la bottom-nav, et reçoit les
 * clics et les évènements tactiles.
 *
 * Le composant centralise aussi ce que chaque overlay refaisait mal ou pas du
 * tout : touche Échap, fermeture par le fond, focus initial, piège à focus,
 * restitution du focus au déclencheur, blocage du défilement d'arrière-plan et
 * nom accessible du dialogue.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/** Racine des overlays : dernier enfant de <body>, hors coque applicative. */
function overlayRoot(): HTMLElement {
  const existing = document.getElementById('overlay-root');
  if (existing) return existing;
  const root = document.createElement('div');
  root.id = 'overlay-root';
  document.body.appendChild(root);
  return root;
}

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export type OverlayVariant = 'feuille' | 'modale' | 'plein';

// Nested dialogs share the scroll lock; only the top dialog owns the keyboard.
const activeOverlays: { panel: HTMLDivElement; trigger: HTMLElement | null }[] = [];
let originalBodyOverflow = '';

/**
 * @param variant `feuille` = bottom sheet (mobile) ; `modale` = boîte centrée ;
 *                `plein` = surface plein écran (recherche).
 */
export function Overlay({
  label,
  onClose,
  variant = 'modale',
  size = 'standard',
  swipeDismiss = false,
  children,
}: {
  label: string;
  onClose: () => void;
  variant?: OverlayVariant;
  /** `large` : formulaires longs ; `fenetre` : la fenêtre des paramètres. Plein écran sur téléphone. */
  size?: 'standard' | 'large' | 'fenetre';
  /** Opt-in for menus/help only; never discard form input through a gesture. */
  swipeDismiss?: boolean;
  children: ReactNode;
}): ReactNode {
  const gesture = useRef<{ x: number; y: number } | undefined>(
    undefined /* React 19 requires this argument. skipcq: JS-W1042 */,
  );
  const panel = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  useEffect(() => {
    close.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const element = panel.current;
    if (!element) return;
    const declencheur = document.activeElement as HTMLElement | null;
    const entry = { panel: element, trigger: declencheur };
    if (activeOverlays.length === 0) originalBodyOverflow = document.body.style.overflow;
    activeOverlays.push(entry);
    document.body.style.overflow = 'hidden';

    // Focus initial DANS le dialogue (sinon le focus resterait sur le
    // déclencheur, derrière l'overlay — piège classique au lecteur d'écran).
    const cibles = (): HTMLElement[] => [
      ...(panel.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []),
    ];
    (cibles()[0] ?? panel.current)?.focus();

    const onKeyDown = (e: KeyboardEvent): void => {
      if (activeOverlays.at(-1) !== entry) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        close.current();
        return;
      }
      if (e.key !== 'Tab') return;
      // Piège à focus : la tabulation ne doit pas sortir vers la page du dessous.
      const items = cibles();
      if (items.length === 0) return;
      const premier = items[0]!;
      const dernier = items[items.length - 1]!;
      const actif = document.activeElement;
      if (e.shiftKey && (actif === premier || !panel.current?.contains(actif))) {
        e.preventDefault();
        dernier.focus();
      } else if (!e.shiftKey && actif === dernier) {
        e.preventDefault();
        premier.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      const wasTop = activeOverlays.at(-1) === entry;
      const index = activeOverlays.indexOf(entry);
      if (index !== -1) activeOverlays.splice(index, 1);
      if (activeOverlays.length === 0) document.body.style.overflow = originalBodyOverflow;
      // Restitution du focus : l'utilisateur revient là où il en était.
      if (wasTop) {
        const top = activeOverlays.at(-1);
        if (declencheur?.isConnected && (!top || top.panel.contains(declencheur))) {
          declencheur.focus();
        } else {
          top?.panel.focus();
        }
      }
    };
  }, []);

  const dialogue = (
    <div
      ref={panel}
      role="dialog"
      aria-modal="true"
      aria-label={label}
      tabIndex={-1}
      className={
        variant === 'feuille'
          ? `sheet${swipeDismiss ? ' sheet--swipe' : ''}`
          : variant === 'modale'
            ? `modal-panel card${size === 'standard' ? '' : ` modal-panel--${size}`}`
            : 'search-overlay'
      }
    >
      {variant === 'feuille' && swipeDismiss ? (
        <button
          type="button"
          className="sheet-dismiss"
          aria-label="Fermer le menu"
          onClick={onClose}
          onTouchStart={(event) => {
            const point = event.touches[0];
            gesture.current =
              event.touches.length === 1 && point
                ? { x: point.clientX, y: point.clientY }
                : undefined;
          }}
          onTouchCancel={() => {
            gesture.current = undefined;
          }}
          onTouchEnd={(event) => {
            const start = gesture.current;
            gesture.current = undefined;
            const end = event.changedTouches[0];
            if (start && end && end.clientY - start.y > 65 && Math.abs(end.clientX - start.x) < 35)
              onClose();
          }}
        >
          <span aria-hidden="true" />
        </button>
      ) : null}
      {children}
    </div>
  );

  // Le plein écran est opaque : il n'a pas besoin d'un fond assombri.
  const contenu =
    variant === 'plein' ? (
      dialogue
    ) : (
      <div
        className={variant === 'feuille' ? 'sheet-backdrop' : 'modal-backdrop'}
        onClick={(e) => {
          if (e.target === e.currentTarget && activeOverlays.at(-1)?.panel === panel.current) {
            onClose();
          }
        }}
      >
        {dialogue}
      </div>
    );

  return createPortal(contenu, overlayRoot());
}
